import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';

import GET_SWITCHES_FOR_ACCOUNT from '@/features/switches/gql/getSwitchesForAccount.gql';
import { useDefaultSwitch } from '@/features/switches/hooks/useDefaultSwitch';
import {
  LoadingSelectorField,
  NoticeSelectorField,
  ReadOnlySelectorField,
} from '@/components/SelectorFieldStates';
import type { SelectorOption } from '@/components/ApiKeySelectorField';
import { PaginatedSelectorInputField } from '@repo/ui/Form';
import type { Mercury } from '@repo/types/Models';

const PAGE_SIZE = 5;

interface SwitchSelectorFieldProps {
  accountId: string;
  value: SelectorOption | undefined;
  onChange: (value: SelectorOption) => void;
  error?: { message?: string };
  label?: string;
  id?: string;
  /** Shown when the account has no switches. */
  emptyMessage?: string;
  /** Called with true when the account has no switches at all. */
  onEmptyChange?: (isEmpty: boolean) => void;
  /**
   * Fill an empty value with the default (oldest) switch, and show a single
   * switch read-only. On for assigning new numbers; off when editing an
   * existing number, so "no switch" stays visibly empty.
   */
  preselectDefault?: boolean;
}

const toOption = (node: { id: string; name: string }, defaultId?: string): SelectorOption => ({
  label: node.id === defaultId ? `${node.name} (default)` : node.name,
  value: node.id,
});

/**
 * Switch picker for one account. The oldest switch is the default: it is
 * preselected, and a single switch is shown read-only.
 */
export const SwitchSelectorField = ({
  accountId,
  value,
  onChange,
  error,
  label = 'Switch',
  id = 'switch_id',
  emptyMessage = 'No switches found for this account.',
  onEmptyChange,
  preselectDefault = true,
}: SwitchSelectorFieldProps) => {
  const [queryVars, setQueryVars] = useState<Mercury.GQL.QueryVariables>({ first: PAGE_SIZE });

  useEffect(() => {
    setQueryVars({ first: PAGE_SIZE });
  }, [accountId]);

  const {
    defaultSwitch,
    loading: defaultLoading,
    error: defaultError,
  } = useDefaultSwitch(accountId);

  const {
    loading,
    data,
    error: queryError,
  } = useQuery(GET_SWITCHES_FOR_ACCOUNT, {
    variables: { id: accountId, ...queryVars },
    notifyOnNetworkStatusChange: true,
  });

  const options: SelectorOption[] =
    data?.switchesForAccount?.edges.map(({ node }) => toOption(node, defaultSwitch?.id)) ?? [];
  const pageInfo = data?.switchesForAccount?.pageInfo;
  const isOnlyPage = !pageInfo?.hasPreviousPage && !pageInfo?.hasNextPage;
  const isSearching = !!queryVars.query;

  const hasSingle = options.length === 1 && isOnlyPage && !isSearching;
  const loadFailed = !!(queryError || defaultError);
  // Only a successful, empty result means the account has no switches
  const hasNone =
    !defaultLoading && !loading && !loadFailed && !defaultSwitch && options.length === 0;

  useEffect(() => {
    onEmptyChange?.(hasNone);
  }, [hasNone, onEmptyChange]);

  useEffect(() => {
    if (preselectDefault && defaultSwitch && !value?.value) {
      onChange(toOption(defaultSwitch, defaultSwitch.id));
    }
  }, [preselectDefault, defaultSwitch, value?.value, onChange]);

  if ((loading && !data) || defaultLoading) return <LoadingSelectorField label={label} />;

  if (loadFailed) {
    if (queryError) console.error(queryError);
    if (defaultError) console.error(defaultError);
    return (
      <NoticeSelectorField label={label}>
        Couldn&apos;t load switches for this account. Please refresh and try again.
      </NoticeSelectorField>
    );
  }

  if (hasNone) return <NoticeSelectorField label={label}>{emptyMessage}</NoticeSelectorField>;

  if (preselectDefault && hasSingle)
    return <ReadOnlySelectorField label={label} value={options[0].label} />;

  return (
    <PaginatedSelectorInputField
      loading={loading}
      id={id}
      label={label}
      onChange={(selected) => onChange(selected as unknown as SelectorOption)}
      value={value}
      error={error}
      options={options}
      pageInfo={pageInfo}
      setQueryVars={setQueryVars}
      pageSize={PAGE_SIZE}
      name="switch"
    />
  );
};
